import React, { useMemo, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import DOMPurify from 'dompurify';
import { toast } from 'react-toastify';
import TipTapEditor from './tiptap/TipTapEditor';
import { uploadImageFile, uploadMediaFile } from './tiptap/extensions/ImageUpload';
import './RichTextEditor.css';

const MAX_UPLOAD_SIZE = 200 * 1024 * 1024; // 200 MB

/**
 * Rich Text Editor component using TipTap (ProseMirror-based).
 *
 * Drop-in replacement for the previous Quill-based editor.
 * Same prop interface — no changes needed at usage sites.
 */
const RichTextEditor = forwardRef(({
  value,
  onChange,
  placeholder = 'Write something...',
  readOnly = false,
  minHeight = 150,
  maxHeight = 400,
  onFocus,
  onBlur,
  className = '',
  // Attachment support
  enableAttachments = false,
  onAttachFiles,
  pendingAttachments = [],
  onRemoveAttachment,
  // Inline image upload support
  enableImageUpload = false,
  imageUploadEntityType = null,
  imageUploadEntityId = null,
  // Mention support
  enableMentions = false,
  projectId = null,
  onMention = null,
}, ref) => {
  const editorRef = useRef(null);

  useImperativeHandle(ref, () => ({
    hasActiveUploads: () => editorRef.current?.hasActiveUploads?.() || false,
  }));
  const imageInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const fileInputRef = useRef(null);

  // Handle image file selection from toolbar picker
  // Uses scoped ref instead of global window events to avoid cross-editor contamination
  const handleImageFileChange = useCallback(async (e) => {
    const file = e.target.files[0];
    if (!file || !imageUploadEntityType || !imageUploadEntityId) return;
    e.target.value = '';

    // Client-side size check
    if (file.size > MAX_UPLOAD_SIZE) {
      const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
      const limitMB = MAX_UPLOAD_SIZE / (1024 * 1024);
      toast.error(`Image "${file.name}" is ${sizeMB} MB and exceeds the ${limitMB} MB upload limit.`);
      return;
    }

    const fileName = file.name;
    const fileSize = file.size;

    // Notify upload start
    editorRef.current?.notifyUploadProgress?.({ type: 'image', fileName, fileSize, percent: 0, status: 'uploading' });

    try {
      const url = await uploadImageFile(file, imageUploadEntityType, imageUploadEntityId, ({ percent }) => {
        editorRef.current?.notifyUploadProgress?.({ type: 'image', fileName, fileSize, percent, status: 'uploading' });
      });
      if (url && editorRef.current) {
        editorRef.current.insertImage(url);
      }
      // Clear progress on success
      editorRef.current?.notifyUploadProgress?.(null);
    } catch (error) {
      console.error('Image upload failed:', error);
      toast.error(error.message || 'Image upload failed.');
      editorRef.current?.notifyUploadProgress?.({ type: 'image', fileName, fileSize, percent: 0, status: 'error' });
      setTimeout(() => editorRef.current?.notifyUploadProgress?.(null), 3000);
    }
  }, [imageUploadEntityType, imageUploadEntityId]);

  // Handle video file selection from toolbar picker
  const handleVideoFileChange = useCallback(async (e) => {
    const file = e.target.files[0];
    if (!file || !imageUploadEntityType || !imageUploadEntityId) return;
    e.target.value = '';

    // Client-side size check
    if (file.size > MAX_UPLOAD_SIZE) {
      const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
      const limitMB = MAX_UPLOAD_SIZE / (1024 * 1024);
      toast.error(`Video "${file.name}" is ${sizeMB} MB and exceeds the ${limitMB} MB upload limit.`);
      return;
    }

    const fileName = file.name;
    const fileSize = file.size;

    // Notify upload start
    editorRef.current?.notifyUploadProgress?.({ type: 'video', fileName, fileSize, percent: 0, status: 'uploading' });

    try {
      const url = await uploadMediaFile(file, imageUploadEntityType, imageUploadEntityId, ({ percent }) => {
        editorRef.current?.notifyUploadProgress?.({ type: 'video', fileName, fileSize, percent, status: 'uploading' });
      });
      if (url && editorRef.current) {
        editorRef.current.insertVideo(url);
      }
      // Clear progress on success
      editorRef.current?.notifyUploadProgress?.(null);
    } catch (error) {
      console.error('Video upload failed:', error);
      toast.error(error.message || 'Video upload failed.');
      editorRef.current?.notifyUploadProgress?.({ type: 'video', fileName, fileSize, percent: 0, status: 'error' });
      setTimeout(() => editorRef.current?.notifyUploadProgress?.(null), 3000);
    }
  }, [imageUploadEntityType, imageUploadEntityId]);

  // Handle attachment file selection
  const handleFileChange = useCallback((e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0 && onAttachFiles) {
      onAttachFiles(files);
    }
    e.target.value = '';
  }, [onAttachFiles]);

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getFileIcon = (type) => {
    if (type.startsWith('image/')) return '🖼️';
    if (type === 'application/pdf') return '📄';
    if (type.includes('word')) return '📝';
    if (type.includes('excel') || type.includes('spreadsheet')) return '📊';
    return '📎';
  };

  return (
    <div className={`rich-text-editor ${readOnly ? 'readonly' : ''} ${className}`}>
      <TipTapEditor
        ref={editorRef}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        readOnly={readOnly}
        minHeight={minHeight}
        maxHeight={maxHeight}
        onFocus={onFocus}
        onBlur={onBlur}
        className={className}
        enableImageUpload={enableImageUpload}
        imageUploadEntityType={imageUploadEntityType}
        imageUploadEntityId={imageUploadEntityId}
        enableAttachments={enableAttachments}
        onAttachFiles={onAttachFiles}
        enableMentions={enableMentions}
        projectId={projectId}
        onMention={onMention}
        imageInputRef={imageInputRef}
        videoInputRef={videoInputRef}
        fileInputRef={fileInputRef}
      />

      {/* Hidden file input for inline image upload */}
      {enableImageUpload && (
        <input
          type="file"
          ref={imageInputRef}
          onChange={handleImageFileChange}
          accept="image/jpeg,image/png,image/gif,image/webp"
          style={{ display: 'none' }}
        />
      )}

      {/* Hidden file input for inline video upload */}
      {enableImageUpload && (
        <input
          type="file"
          ref={videoInputRef}
          onChange={handleVideoFileChange}
          accept="video/mp4,video/quicktime,video/webm"
          style={{ display: 'none' }}
        />
      )}

      {/* Hidden file input for attachments */}
      {enableAttachments && (
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          multiple
          style={{ display: 'none' }}
        />
      )}

      {/* Pending attachments preview */}
      {enableAttachments && pendingAttachments.length > 0 && (
        <div className="editor-pending-attachments">
          {pendingAttachments.map((file, index) => (
            <div key={index} className="pending-attachment-chip">
              <span className="attachment-chip-icon">{getFileIcon(file.type)}</span>
              <span className="attachment-chip-name" title={file.name}>{file.name}</span>
              <span className="attachment-chip-size">{formatFileSize(file.size)}</span>
              {onRemoveAttachment && (
                <button
                  type="button"
                  className="attachment-chip-remove"
                  onClick={() => onRemoveAttachment(index)}
                  title="Remove"
                >
                  ×
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
});

RichTextEditor.displayName = 'RichTextEditor';

/**
 * Rich Text Display component for rendering HTML content safely.
 * Uses DOMPurify for XSS protection.
 */
export const RichTextDisplay = ({ content, className = '' }) => {
  const sanitizedContent = useMemo(() => {
    if (!content) return '';
    return DOMPurify.sanitize(content, {
      ADD_TAGS: ['img', 'video', 'source'],
      ADD_ATTR: ['data-alignment', 'data-mention-id', 'data-ticket-key', 'data-ticket-id', 'data-ticket-title', 'data-ticket-status', 'data-project-id', 'width', 'style', 'target', 'rel', 'class', 'controls', 'preload', 'src', 'type'],
      ALLOW_DATA_ATTR: true,
    });
  }, [content]);

  if (!sanitizedContent) return null;

  return (
    <div
      className={`rich-text-display ${className}`}
      dangerouslySetInnerHTML={{ __html: sanitizedContent }}
    />
  );
};

/**
 * Simple text input that can toggle to rich text.
 * (Legacy export — currently unused in the codebase)
 */
export const TextAreaWithRichText = ({
  value,
  onChange,
  useRichText = true,
  placeholder,
  readOnly,
  minHeight,
  maxHeight,
  className,
}) => {
  if (!useRichText) {
    return (
      <textarea
        className={`form-input ${className}`}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        readOnly={readOnly}
        style={{ minHeight: minHeight || 100 }}
      />
    );
  }

  return (
    <RichTextEditor
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      readOnly={readOnly}
      minHeight={minHeight}
      maxHeight={maxHeight}
      className={className}
    />
  );
};

export default RichTextEditor;
